import React, { useEffect, useMemo, useState } from "react";
import { Calendar, User, Clock, Video, MapPin, RefreshCw, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";

const STATUS_STYLE = {
  proposed: "border-amber-300 bg-amber-50 text-amber-800",
  accepted: "border-blue-300 bg-blue-50 text-blue-800",
  completed: "border-slate-300 bg-slate-100 text-slate-800",
  signed: "border-emerald-300 bg-emerald-50 text-emerald-800",
};

const STATUS_LABEL = {
  proposed: "Vorschlag",
  accepted: "Angenommen",
  completed: "Wartet auf Kunde",
  signed: "Unterschrieben",
};

export default function WochenUebersichtPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [coachFilter, setCoachFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/appointments/weekly-overview");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReminders = async () => {
    setSending(true);
    try {
      const r = await api.post("/appointments/send-reminders-now");
      toast.success(`${r.data.reminders_sent} Erinnerung(en) versendet`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  const weeks = useMemo(() => {
    if (!data) return [];
    // group days (28) into 4 weeks
    const chunks = [];
    for (let i = 0; i < data.days.length; i += 7) chunks.push(data.days.slice(i, i + 7));
    return chunks;
  }, [data]);

  const filterAppts = (list) => coachFilter === "all" ? list : list.filter((a) => a.coach_user_id === coachFilter);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1600px] mx-auto" data-testid="wochen-uebersicht-root">
      <div className="flex items-end justify-between mb-8 border-b border-slate-200 pb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">AVGS · Admin</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            Wochenübersicht
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            Alle anstehenden Termine der nächsten 4 Wochen — über alle Coaches hinweg. Grün = unterschrieben.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {data?.coaches && data.coaches.length > 0 && (
            <select
              data-testid="coach-filter"
              className="input-tech max-w-xs"
              value={coachFilter}
              onChange={(e) => setCoachFilter(e.target.value)}
            >
              <option value="all">Alle Coaches ({data.coaches.length})</option>
              {data.coaches.map((c) => (
                <option key={c.user_id} value={c.user_id}>{c.name}</option>
              ))}
            </select>
          )}
          <button
            data-testid="reload-btn"
            onClick={load}
            className="px-4 py-3 border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm flex items-center gap-2"
          >
            <RefreshCw size={14} strokeWidth={1.5} /> Neu laden
          </button>
          <button
            data-testid="send-reminders-btn"
            onClick={sendReminders}
            disabled={sending}
            className="px-4 py-3 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 text-sm flex items-center gap-2"
          >
            <Mail size={14} strokeWidth={1.5} /> {sending ? "Sende…" : "Erinnerungen für morgen"}
          </button>
        </div>
      </div>

      {loading && <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Lädt…</div>}

      {data && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white mb-6" data-testid="week-stats">
            <div className="p-5 border-b md:border-b-0 md:border-r border-slate-200">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">Termine gesamt</div>
              <div className="font-heading text-4xl font-light tracking-tighter text-slate-900">{data.stats.total_appointments}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                {formatDeShort(data.range.from)} – {formatDeShort(data.range.to)}
              </div>
            </div>
            <div className="p-5 border-b md:border-b-0 md:border-r border-slate-200">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">UE geplant</div>
              <div className="font-heading text-4xl font-light tracking-tighter text-slate-900">{data.stats.total_ue_upcoming}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">noch nicht durchgeführt</div>
            </div>
            <div className="p-5 border-b md:border-b-0 md:border-r border-slate-200">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">Unterschrieben</div>
              <div className="font-heading text-4xl font-light tracking-tighter text-emerald-700">{data.stats.signed}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">im Zeitraum</div>
            </div>
            <div className="p-5">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">Wartet auf Kunde</div>
              <div className="font-heading text-4xl font-light tracking-tighter text-amber-700">{data.stats.waiting_for_client_signature}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">Unterschrift ausstehend</div>
            </div>
          </div>

          {/* 4 Weeks Grid */}
          <div className="space-y-6">
            {weeks.map((week, wi) => {
              const from = new Date(week[0].date).toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
              const to = new Date(week[6].date).toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
              const weekAppts = week.reduce((acc, d) => acc + filterAppts(d.appointments).length, 0);
              return (
                <div key={wi} data-testid={`week-${wi}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="text-xs font-bold uppercase tracking-overline text-slate-500">
                      Woche {wi + 1} · {from} – {to}
                    </h3>
                    <span className="text-xs text-slate-500 font-mono">{weekAppts} Termine</span>
                  </div>
                  <div className="grid grid-cols-7 border border-slate-200 bg-white">
                    {week.map((day) => {
                      const appts = filterAppts(day.appointments);
                      return (
                        <div
                          key={day.date}
                          className={`border-r last:border-r-0 border-slate-200 min-h-[130px] p-2 ${day.is_weekend ? "bg-slate-50" : ""} ${day.is_today ? "ring-2 ring-slate-900 ring-inset" : ""}`}
                          data-testid={`day-${day.date}`}
                        >
                          <div className="flex items-baseline justify-between mb-2">
                            <div className={`text-[10px] font-bold uppercase tracking-overline ${day.is_today ? "text-slate-900" : "text-slate-500"}`}>
                              {day.weekday}
                            </div>
                            <div className={`font-heading text-lg font-light tracking-tighter ${day.is_today ? "text-slate-900" : "text-slate-600"}`}>
                              {day.date.slice(8, 10)}
                            </div>
                          </div>
                          <div className="space-y-1">
                            {appts.slice(0, 4).map((a) => (
                              <div
                                key={a.appointment_id}
                                className={`text-[10px] border p-1.5 leading-tight ${STATUS_STYLE[a.status] || STATUS_STYLE.proposed}`}
                                title={`${a.client_name} · ${a.coach_name} · ${a.planned_ue} UE · ${STATUS_LABEL[a.status]}`}
                                data-testid={`day-appt-${a.appointment_id}`}
                              >
                                <div className="flex items-center gap-1 font-mono">
                                  {a.time_from && <span>{a.time_from.slice(0, 5)}</span>}
                                  {a.mode === "online" ? <Video size={9} strokeWidth={2} /> : <MapPin size={9} strokeWidth={2} />}
                                  <span className="ml-auto">{a.planned_ue}UE</span>
                                </div>
                                <div className="font-medium truncate">{a.client_name}</div>
                                <div className="text-[9px] opacity-70 truncate">{a.coach_name}</div>
                              </div>
                            ))}
                            {appts.length > 4 && (
                              <div className="text-[10px] text-slate-500 text-center font-mono">+{appts.length - 4} weitere</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {data.stats.total_appointments === 0 && (
            <div className="border border-slate-200 bg-white p-10 text-center mt-6">
              <Calendar size={32} strokeWidth={1} className="text-slate-300 mx-auto mb-4" />
              <p className="text-sm text-slate-600">
                Keine Termine in den nächsten 4 Wochen. Coaches können unter &bdquo;Termine&ldquo; neue Termine vorschlagen.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
